/**
 * 拍摄计划剪贴板缓存管理
 * 按每次拖入聚合为 ClipboardGroup，按 scheduleID 存 localStorage v3（type: SHOOTS | NOTE）
 */
import { useState, useCallback, useEffect } from 'react';
import type { SlotInfo } from '../../../types/schedule.types';
import { buildDisplaySegments } from '../../../utils/calDateSlotsSortUtils';
import type { SceneInfo } from '../../../types/scene.types';

/** 剪贴板单条：拍摄虚拟段（同日历地点头连续 SHOOT 行，SHOOTS）或便签（NOTE） */
export interface ClipboardScheduleItem {
  type: 'SHOOTS' | 'NOTE';
  location?: string;
  shift?: string;
  slots: SlotInfo[];
}

/** 一次拖入剪贴板对应的一组 */
export interface ClipboardGroup {
  id: string;
  sourceDateStr: string;
  /** 来源日的 calDate.calDateShift（整日/卡片拖入剪贴板时快照） */
  sourceCalDateShift?: string;
  items: ClipboardScheduleItem[];
}

const STORAGE_KEY_PREFIX = 'schedule-clipboard-';
const STORAGE_VERSION = 3;

function getStorageKey(scheduleID: string): string {
  return `${STORAGE_KEY_PREFIX}${scheduleID}`;
}

function newGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `g-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function isClipboardScheduleItem(obj: unknown): obj is ClipboardScheduleItem {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  if (typeof o.id === 'string' && 'sourceDateStr' in o && Array.isArray(o.items)) {
    return false;
  }
  return (o.type === 'SHOOTS' || o.type === 'NOTE') && Array.isArray(o.slots);
}

function isClipboardGroupShape(obj: unknown): obj is ClipboardGroup {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.sourceDateStr === 'string' &&
    Array.isArray(o.items) &&
    o.items.every((it) => isClipboardScheduleItem(it))
  );
}

/** 从 localStorage 原始 JSON 解析为 ClipboardGroup[]（仅接受当前 STORAGE_VERSION） */
export function parseClipboardStorage(raw: string | null): ClipboardGroup[] {
  if (!raw || raw.trim() === '') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const rec = parsed as Record<string, unknown>;
      if (rec.version !== STORAGE_VERSION || !Array.isArray(rec.groups)) {
        return [];
      }
      const groups = rec.groups.filter(isClipboardGroupShape);
      return groups.length > 0 ? groups : [];
    }
  } catch {
    return [];
  }
  return [];
}

function serializeGroups(groups: ClipboardGroup[]): string {
  return JSON.stringify({ version: STORAGE_VERSION, groups });
}

export function useScheduleClipboard(scheduleID: string | undefined) {
  const [groups, setGroups] = useState<ClipboardGroup[]>([]);

  const loadFromStorage = useCallback(() => {
    if (!scheduleID) {
      setGroups([]);
      return;
    }
    try {
      const raw = localStorage.getItem(getStorageKey(scheduleID));
      setGroups(parseClipboardStorage(raw));
    } catch {
      setGroups([]);
    }
  }, [scheduleID]);

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  const persist = useCallback(
    (next: ClipboardGroup[]) => {
      if (!scheduleID) return;
      try {
        if (next.length === 0) {
          localStorage.removeItem(getStorageKey(scheduleID));
        } else {
          localStorage.setItem(getStorageKey(scheduleID), serializeGroups(next));
        }
      } catch (e) {
        console.warn('剪贴板写入失败:', e);
      }
    },
    [scheduleID],
  );

  const addGroup = useCallback(
    (input: {
      sourceDateStr: string;
      items: ClipboardScheduleItem[];
      sourceCalDateShift?: string;
    }) => {
      if (!scheduleID || !input.items.length) return;
      const group: ClipboardGroup = {
        id: newGroupId(),
        sourceDateStr: input.sourceDateStr ?? '',
        items: input.items,
        ...(input.sourceCalDateShift !== undefined
          ? { sourceCalDateShift: input.sourceCalDateShift }
          : {}),
      };
      setGroups((prev) => {
        const next = [...prev, group];
        persist(next);
        return next;
      });
    },
    [scheduleID, persist],
  );

  const removeGroupAt = useCallback(
    (groupIndex: number) => {
      if (!scheduleID) return;
      setGroups((prev) => {
        if (groupIndex < 0 || groupIndex >= prev.length) return prev;
        const next = prev.filter((_, i) => i !== groupIndex);
        persist(next);
        return next;
      });
    },
    [scheduleID, persist],
  );

  const removeGroupItemAt = useCallback(
    (groupIndex: number, itemIndex: number) => {
      if (!scheduleID) return;
      setGroups((prev) => {
        if (groupIndex < 0 || groupIndex >= prev.length) return prev;
        const g = prev[groupIndex];
        if (itemIndex < 0 || itemIndex >= g.items.length) return prev;
        const newItems = g.items.filter((_, i) => i !== itemIndex);
        let next: ClipboardGroup[];
        if (newItems.length === 0) {
          next = prev.filter((_, i) => i !== groupIndex);
        } else {
          next = prev.map((gr, i) =>
            i === groupIndex ? { ...gr, items: newItems } : gr,
          );
        }
        persist(next);
        return next;
      });
    },
    [scheduleID, persist],
  );

  const removeGroupItemSlotAt = useCallback(
    (groupIndex: number, itemIndex: number, slotIndex: number) => {
      if (!scheduleID) return;
      setGroups((prev) => {
        const group = prev[groupIndex];
        const item = group?.items[itemIndex];
        if (!item || slotIndex < 0 || slotIndex >= item.slots.length) return prev;
        const slots = item.slots.filter((_, i) => i !== slotIndex);
        const items = slots.length
          ? group.items.map((entry, i) => i === itemIndex ? { ...entry, slots } : entry)
          : group.items.filter((_, i) => i !== itemIndex);
        const next = items.length
          ? prev.map((entry, i) => i === groupIndex ? { ...entry, items } : entry)
          : prev.filter((_, i) => i !== groupIndex);
        persist(next);
        return next;
      });
    },
    [scheduleID, persist],
  );

  const clear = useCallback(() => {
    if (!scheduleID) return;
    setGroups([]);
    try {
      localStorage.removeItem(getStorageKey(scheduleID));
    } catch (e) {
      console.warn('剪贴板清空失败:', e);
    }
  }, [scheduleID]);

  return {
    groups,
    addGroup,
    removeGroupAt,
    removeGroupItemAt,
    removeGroupItemSlotAt,
    clear,
    refresh: loadFromStorage,
  };
}

/** 将 slots + calDateSlotsSort（扁平）转为 ClipboardScheduleItem[] */
export function slotsToClipboardItems(
  slots: SlotInfo[],
  calDateSlotsSort: string[] | undefined,
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined,
): ClipboardScheduleItem[] {
  if (slots.length === 0) return [];
  const slotMap = new Map(slots.map((t) => [t.slotID, t]));
  const dateStr =
    slots.find((t) => t.slotDate)?.slotDate?.split('T')[0] ?? 'clipboard';
  const segments = buildDisplaySegments(
    slots,
    calDateSlotsSort,
    dateStr,
    (sid) => {
      const s = getSceneInfo(sid);
      return s ? { sceneLocation: s.sceneLocation } : undefined;
    },
  );
  const result: ClipboardScheduleItem[] = [];
  for (const seg of segments) {
    if (seg.kind === 'note') {
      for (const id of seg.items) {
        const t = slotMap.get(id);
        if (t) result.push({ type: 'NOTE', slots: [t] });
      }
    } else {
      const validSolts = seg.items
        .map((id) => slotMap.get(id))
        .filter((t): t is SlotInfo => !!t);
      if (validSolts.length === 0) continue;
      result.push({
        type: 'SHOOTS',
        location: seg.locationLabel ?? '',
        shift: seg.shift,
        slots: validSolts,
      });
    }
  }
  return result;
}
